import { useState, useEffect } from 'react'
import { useNavigate, Link, useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Pencil } from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { clientsApi, customFieldsApi, countryMasterApi, categoryMasterApi } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { usePermissions } from '@/contexts/PermissionContext'

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import type { CreateClientRequest, FieldConfiguration } from '@/types'
import { validateRegex, getApiErrorMessage, isValidEmail, sanitizePhoneNumber } from '@/lib/utils'

interface ClientFormPageProps {
  mode?: 'create' | 'view' | 'edit'
}

export default function CreateClientPage({ mode: propMode }: ClientFormPageProps = {}) {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  const { canAddEdit } = usePermissions()

  // Determine mode based on props or URL
  const isViewMode = propMode === 'view' || (!!id && propMode !== 'edit')
  const isCreateMode = !id && !propMode

  const [isEditing, setIsEditing] = useState(!isViewMode)

  const { data: clientFields = [] } = useQuery({
    queryKey: ['customFields', 'CLIENT'],
    queryFn: () => customFieldsApi.getAll('CLIENT'),
  })

  // Fetch countries for dropdown
  const { data: countries = [] } = useQuery({
    queryKey: ['countries'],
    queryFn: () => countryMasterApi.getAll(true), // Only active countries
  })

  // Fetch client categories for dropdown
  const { data: clientCategories = [] } = useQuery({
    queryKey: ['categories', 'CLIENT'],
    queryFn: () => categoryMasterApi.getAll(true, 'CLIENT'), // Only active CLIENT categories
  })

  // Fetch existing client data if we have an ID
  const { data: existingClient, isLoading: isLoadingClient } = useQuery({
    queryKey: ['client', id],
    queryFn: () => clientsApi.getById(id!),
    enabled: !!id,
  })

  const [formData, setFormData] = useState<CreateClientRequest>({
    client_name: '',
    client_email: '',
    country: '',
    currency: 'USD',
    custom_fields: {},
  })
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const [clientNameError, setClientNameError] = useState<string>('')

  // Populate form with existing client data
  useEffect(() => {
    if (existingClient) {
      // Convert custom_fields from object with config_id keys to simple key-value pairs
      const customFieldsData: Record<string, string> = {}
      if (existingClient.custom_fields) {
        Object.entries(existingClient.custom_fields).forEach(([configId, field]) => {
          customFieldsData[configId] = field.field_value || ''
        })
      }

      setFormData({
        client_name: existingClient.client_name,
        client_email: existingClient.client_email,
        client_phone: existingClient.client_phone,
        country: existingClient.country,
        currency: existingClient.currency,
        address_line1: existingClient.address_line1,
        address_line2: existingClient.address_line2,
        city: existingClient.city,
        state: existingClient.state,
        postal_code: existingClient.postal_code,
        payment_terms: existingClient.payment_terms,
        tax_id: existingClient.tax_id,
        category_id: existingClient.category_id,
        custom_fields: customFieldsData,
      })

      // Validate loaded custom fields
      const errors: Record<string, string> = {}
      clientFields.forEach((field) => {
        const value = customFieldsData[field.config_id] || ''
        const error = validateCustomField(field, value)
        if (error) {
          errors[field.config_id] = error
        }
      })
      setFieldErrors(errors)
    }
  }, [existingClient, clientFields])

  const createMutation = useMutation({
    mutationFn: clientsApi.create,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['clients'] })
      toast.success(t('clients.clientCreated'))
      navigate(`/clients/${data.client_id}`)
    },
    onError: (error: any) => {
      const errorMessage = getApiErrorMessage(error, t('clients.failedToCreate'))
      toast.error(errorMessage)
    },
  })

  const updateMutation = useMutation({
    mutationFn: (data: Partial<CreateClientRequest>) => clientsApi.update(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client', id] })
      queryClient.invalidateQueries({ queryKey: ['clients'] })
      toast.success(t('clients.clientUpdated'))
      setIsEditing(false)
    },
    onError: (error: any) => {
      const errorMessage = getApiErrorMessage(error, t('clients.failedToUpdate'))
      toast.error(errorMessage)
    },
  })

  // Validate client name contains only alphabets and spaces
  const validateClientName = (name: string): string | null => {
    if (!name) return null // Required validation is handled separately
    const clientNameRegex = /^[a-zA-Z\s]+$/
    if (!clientNameRegex.test(name)) {
      return t('clients.clientNameInvalid')
    }
    return null
  }

  const validateCustomField = (field: FieldConfiguration, value: string): string | null => {
    if (field.is_mandatory && !value) {
      return `${field.field_label} is required`
    }
    if (field.regex_pattern && value && !validateRegex(value, field.regex_pattern)) {
      return `${field.field_label} must match pattern: ${field.regex_pattern}`
    }
    if (field.data_type === 'NUMBER' && value && isNaN(Number(value))) {
      return `${field.field_label} must be a number`
    }
    if (field.data_type === 'DATE' && value) {
      // Validate date format - must be YYYY-MM-DD or other accepted formats
      const dateFormats = [
        /^\d{4}-\d{2}-\d{2}$/,                    // YYYY-MM-DD
        /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z$/, // ISO format
        /^\d{2}\/\d{2}\/\d{4}$/,                  // MM/DD/YYYY
        /^\d{2}-\d{2}-\d{4}$/,                    // DD-MM-YYYY
      ]
      const isValidFormat = dateFormats.some(format => format.test(value))
      if (!isValidFormat) {
        return `${field.field_label} must be in format YYYY-MM-DD`
      }
      // Also check if it's a valid date
      const date = new Date(value)
      if (isNaN(date.getTime())) {
        return `${field.field_label} must be a valid date`
      }
    }
    return null
  }

  const handleCustomFieldChange = (configId: string, value: string, field: FieldConfiguration) => {
    setFormData({
      ...formData,
      custom_fields: { ...formData.custom_fields, [configId]: value },
    })

    const error = validateCustomField(field, value)
    setFieldErrors((prev) => {
      if (error) {
        return { ...prev, [configId]: error }
      }
      const { [configId]: _, ...rest } = prev
      return rest
    })
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    // Validate client name (alphabets only)
    const clientNameValidationError = validateClientName(formData.client_name)
    if (clientNameValidationError) {
      setClientNameError(clientNameValidationError)
      // Scroll to client name field
      const element = document.getElementById('client_name')
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' })
        element.focus()
      }
      return
    }
    setClientNameError('')

    // Validate email format if provided
    if (formData.client_email && formData.client_email.trim() && !isValidEmail(formData.client_email)) {
      toast.error(t('clients.invalidEmail', 'Please enter a valid email address'))
      const element = document.getElementById('client_email')
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' })
        element.focus()
      }
      return
    }

    // Validate all custom fields (mandatory and regex)
    const errors: Record<string, string> = {}
    clientFields.forEach((field) => {
      const value = formData.custom_fields?.[field.config_id] || ''
      const error = validateCustomField(field, value)
      if (error) {
        errors[field.config_id] = error
      }
    })

    // Also check existing fieldErrors from real-time validation
    const allErrors = { ...fieldErrors, ...errors }

    if (Object.keys(allErrors).length > 0) {
      setFieldErrors(allErrors)
      // Scroll to first error field
      const firstErrorField = Object.keys(allErrors)[0]
      const element = document.getElementById(firstErrorField)
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' })
        element.focus()
      }
      return
    }

    if (isCreateMode) {
      createMutation.mutate(formData)
    } else if (id) {
      updateMutation.mutate(formData)
    }
  }

  const isFormValid = () => {
    if (!formData.client_name || !formData.client_email || !formData.country || !formData.currency) return false
    if (Object.keys(fieldErrors).length > 0) return false
    if (clientNameError) return false
    // Validate client name format
    if (validateClientName(formData.client_name)) return false

    // Check mandatory fields
    for (const field of clientFields) {
      if (field.is_mandatory && !formData.custom_fields?.[field.config_id]) {
        return false
      }
    }
    return true
  }

  // Show loading state when fetching existing client
  if (id && isLoadingClient) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-muted-foreground">{t('clients.loadingClient')}</div>
      </div>
    )
  }

  const pageTitle = isCreateMode ? t('clients.createClient') : (isEditing ? t('clients.editClient') : t('clients.clientDetails'))
  const pageDescription = isCreateMode
    ? t('clients.addNewClient')
    : (isEditing ? t('clients.updateClientInfo') : t('clients.viewClientInfo'))

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/clients">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <div>
            <h1 className="text-3xl font-bold">{pageTitle}</h1>
            <p className="text-muted-foreground">{pageDescription}</p>
          </div>
        </div>
        {id && !isEditing && canAddEdit('CLIENTS') && (
          <div className="flex gap-2">
            <Button onClick={() => setIsEditing(true)} variant="outline">
              <Pencil className="mr-2 h-4 w-4" />
              {t('common.edit')}
            </Button>
          </div>
        )}
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Basic Information Section */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-primary border-b pb-2">{t('clients.basicInfo')}</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="client_name">
                {t('clients.clientName')} {isEditing && <span className="text-destructive">*</span>}
              </Label>
              <Input
                id="client_name"
                value={formData.client_name}
                onChange={(e) => {
                  const value = e.target.value
                  setFormData({ ...formData, client_name: value })
                  // Real-time validation for client name
                  const error = validateClientName(value)
                  setClientNameError(error || '')
                }}
                placeholder={t('clients.enterClientName')}
                required={isEditing}
                disabled={!isEditing}
                className={`bg-white dark:bg-zinc-900 border-gray-300 dark:border-zinc-700 ${clientNameError ? 'border-destructive' : ''}`}
              />
              {clientNameError && (
                <p className="text-sm text-destructive">{clientNameError}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="client_email">
                {t('clients.email')} {isEditing && <span className="text-destructive">*</span>}
              </Label>
              <Input
                id="client_email"
                type="email"
                value={formData.client_email}
                onChange={(e) => setFormData({ ...formData, client_email: e.target.value })}
                placeholder={t('clients.emailPlaceholder')}
                required={isEditing}
                disabled={!isEditing}
                className="bg-white dark:bg-zinc-900 border-gray-300 dark:border-zinc-700"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="client_phone">{t('clients.phone')}</Label>
              <Input
                id="client_phone"
                value={formData.client_phone || ''}
                onChange={(e) => setFormData({ ...formData, client_phone: sanitizePhoneNumber(e.target.value) })}
                placeholder={t('clients.phonePlaceholder')}
                disabled={!isEditing}
                className="bg-white dark:bg-zinc-900 border-gray-300 dark:border-zinc-700"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="country">
                {t('clients.country')} {isEditing && <span className="text-destructive">*</span>}
              </Label>
              <Select
                value={formData.country}
                onValueChange={(v) => setFormData({ ...formData, country: v })}
                disabled={!isEditing}
              >
                <SelectTrigger className="bg-white dark:bg-zinc-900 border-gray-300 dark:border-zinc-700">
                  <SelectValue placeholder={t('clients.selectCountry')} />
                </SelectTrigger>
                <SelectContent>
                  {countries.map((country) => (
                    <SelectItem key={country.country_id} value={country.country_code}>
                      {country.country_name} ({country.country_code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="currency">
                {t('clients.currency')} {isEditing && <span className="text-destructive">*</span>}
              </Label>
              <Select
                value={formData.currency}
                onValueChange={(v) => setFormData({ ...formData, currency: v })}
                disabled={!isEditing}
              >
                <SelectTrigger className="bg-white dark:bg-zinc-900 border-gray-300 dark:border-zinc-700">
                  <SelectValue placeholder={t('clients.selectCurrency')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="USD">USD - US Dollar</SelectItem>
                  <SelectItem value="EUR">EUR - Euro</SelectItem>
                  <SelectItem value="GBP">GBP - British Pound</SelectItem>
                  <SelectItem value="INR">INR - Indian Rupee</SelectItem>
                  <SelectItem value="AUD">AUD - Australian Dollar</SelectItem>
                  <SelectItem value="CAD">CAD - Canadian Dollar</SelectItem>
                  <SelectItem value="JPY">JPY - Japanese Yen</SelectItem>
                  <SelectItem value="CNY">CNY - Chinese Yuan</SelectItem>
                  <SelectItem value="CHF">CHF - Swiss Franc</SelectItem>
                  <SelectItem value="SGD">SGD - Singapore Dollar</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="tax_id">{t('clients.taxId')}</Label>
              <Input
                id="tax_id"
                value={formData.tax_id || ''}
                onChange={(e) => setFormData({ ...formData, tax_id: e.target.value })}
                placeholder={t('clients.taxIdPlaceholder')}
                disabled={!isEditing}
                className="bg-white dark:bg-zinc-900 border-gray-300 dark:border-zinc-700"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="category_id">{t('clients.category')}</Label>
              <Select
                value={formData.category_id || '__none__'}
                onValueChange={(v) => setFormData({ ...formData, category_id: v === '__none__' ? undefined : v })}
                disabled={!isEditing}
              >
                <SelectTrigger className="bg-white dark:bg-zinc-900 border-gray-300 dark:border-zinc-700">
                  <SelectValue placeholder={t('clients.selectCategory')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">{t('common.none')}</SelectItem>
                  {clientCategories.map((category) => (
                    <SelectItem key={category.category_id} value={category.category_id}>
                      {category.category_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Address Details Section */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-primary border-b pb-2">{t('clients.addressDetails')}</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="address_line1">{t('clients.addressLine1')}</Label>
              <Input
                id="address_line1"
                value={formData.address_line1 || ''}
                onChange={(e) => setFormData({ ...formData, address_line1: e.target.value })}
                placeholder={t('clients.addressLine1Placeholder')}
                disabled={!isEditing}
                className="bg-white dark:bg-zinc-900 border-gray-300 dark:border-zinc-700"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="address_line2">{t('clients.addressLine2')}</Label>
              <Input
                id="address_line2"
                value={formData.address_line2 || ''}
                onChange={(e) => setFormData({ ...formData, address_line2: e.target.value })}
                placeholder={t('clients.addressLine2Placeholder')}
                disabled={!isEditing}
                className="bg-white dark:bg-zinc-900 border-gray-300 dark:border-zinc-700"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="city">{t('clients.city')}</Label>
              <Input
                id="city"
                value={formData.city || ''}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                placeholder={t('clients.cityPlaceholder')}
                disabled={!isEditing}
                className="bg-white dark:bg-zinc-900 border-gray-300 dark:border-zinc-700"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="state">{t('clients.state')}</Label>
              <Input
                id="state"
                value={formData.state || ''}
                onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                placeholder={t('clients.statePlaceholder')}
                disabled={!isEditing}
                className="bg-white dark:bg-zinc-900 border-gray-300 dark:border-zinc-700"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="postal_code">{t('clients.postalCode')}</Label>
              <Input
                id="postal_code"
                value={formData.postal_code || ''}
                onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                placeholder={t('clients.postalCodePlaceholder')}
                disabled={!isEditing}
                className="bg-white dark:bg-zinc-900 border-gray-300 dark:border-zinc-700"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="payment_terms">{t('clients.paymentTerms')}</Label>
              <Input
                id="payment_terms"
                value={formData.payment_terms || ''}
                onChange={(e) => setFormData({ ...formData, payment_terms: e.target.value })}
                placeholder={t('clients.paymentTermsPlaceholder')}
                disabled={!isEditing}
                className="bg-white dark:bg-zinc-900 border-gray-300 dark:border-zinc-700"
              />
            </div>
          </div>
        </div>

        {/* Custom Fields Section */}
        {clientFields.length > 0 && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-primary border-b pb-2">{t('clients.customFields')}</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {clientFields.map((field) => (
                <div key={field.config_id} className="space-y-2">
                  <Label htmlFor={field.config_id}>
                    {field.field_label}
                    {isEditing && field.is_mandatory && <span className="text-destructive"> *</span>}
                  </Label>
                  {field.data_type === 'DROPDOWN' ? (
                    <Select
                      value={formData.custom_fields?.[field.config_id] || ''}
                      onValueChange={(v) => handleCustomFieldChange(field.config_id, v, field)}
                      disabled={!isEditing}
                    >
                      <SelectTrigger className="bg-white dark:bg-zinc-900 border-gray-300 dark:border-zinc-700">
                        <SelectValue placeholder={t('common.select')} />
                      </SelectTrigger>
                      <SelectContent>
                        {field.dropdown_options?.map((option) => (
                          <SelectItem key={option} value={option}>
                            {option}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input
                      id={field.config_id}
                      type={field.data_type === 'DATE' ? 'date' : field.data_type === 'NUMBER' ? 'number' : 'text'}
                      value={formData.custom_fields?.[field.config_id] || ''}
                      onChange={(e) => handleCustomFieldChange(field.config_id, e.target.value, field)}
                      disabled={!isEditing}
                      className={`bg-white dark:bg-zinc-900 ${
                        fieldErrors[field.config_id]
                          ? 'border-destructive focus-visible:ring-destructive'
                          : 'border-gray-300 dark:border-zinc-700'
                      }`}
                    />
                  )}
                  {isEditing && fieldErrors[field.config_id] && (
                    <p className="text-sm text-destructive font-medium">{fieldErrors[field.config_id]}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Form Actions */}
        {isEditing && (
          <div className="flex justify-center gap-4 pt-6 border-t">
            {id ? (
              <Button type="button" variant="outline" onClick={() => setIsEditing(false)}>
                {t('common.cancel')}
              </Button>
            ) : (
              <Link to="/clients">
                <Button type="button" variant="outline">{t('common.cancel')}</Button>
              </Link>
            )}
            <Button type="submit" disabled={!isFormValid() || createMutation.isPending || updateMutation.isPending}>
              {createMutation.isPending ? t('clients.creating') :
               updateMutation.isPending ? t('clients.saving') :
               (isCreateMode ? t('common.submit') : t('clients.saveChanges'))}
            </Button>
          </div>
        )}
      </form>
    </div>
  )
}
